import { useDeferredValue, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Plus } from "lucide-react";
import { useClients } from "@/api/clients";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { buttonClass } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/page-header";

export function ClientsPage() {
  const [query, setQuery] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const search = useDeferredValue(query.trim());
  const clients = useClients(search, includeArchived);

  const newClient = (
    <Link to="/clients/new" className={buttonClass()}>
      <Plus className="h-4 w-4" aria-hidden />
      New client
    </Link>
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Clients"
        description="Customers you invoice, with their contacts and rate cards."
        actions={newClient}
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
        <Input
          type="search"
          placeholder="Search by code or name"
          aria-label="Search clients"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="sm:max-w-xs"
        />
        <Checkbox
          id="clients-show-archived"
          label="Show archived"
          checked={includeArchived}
          onChange={setIncludeArchived}
        />
      </div>

      {clients.isPending ? (
        <p className="text-muted-foreground">Loading clients…</p>
      ) : clients.isError ? (
        <RequestError error={clients.error} />
      ) : clients.data.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={search ? "No clients match your search" : "No clients yet"}
          description="Add a client to record its contacts and rate card."
          action={search ? undefined : newClient}
        />
      ) : (
        <>
          <ul className="flex flex-col gap-3 md:hidden">
            {clients.data.map((client) => (
              <li key={client.id}>
                <Link
                  to={`/clients/${client.id}`}
                  className="flex flex-col gap-1 rounded-lg border bg-card p-4 shadow-sm"
                >
                  <span className="flex items-center gap-2">
                    <span className="font-semibold text-primary">{client.code}</span>
                    {client.archived_at && <Badge variant="muted">Archived</Badge>}
                  </span>
                  <span className="text-sm">{client.legal_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {client.country_code} · {client.currency} · {client.payment_days} days
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="table-frame hidden md:block">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Legal name</th>
                  <th>Country</th>
                  <th>Currency</th>
                  <th className="text-right">Payment days</th>
                  <th>
                    <span className="sr-only">Status</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {clients.data.map((client) => (
                  <tr key={client.id}>
                    <td className="font-medium">
                      <Link to={`/clients/${client.id}`} className="text-primary hover:underline">
                        {client.code}
                      </Link>
                    </td>
                    <td>{client.legal_name}</td>
                    <td>{client.country_code}</td>
                    <td>{client.currency}</td>
                    <td className="text-right">{client.payment_days}</td>
                    <td className="text-right">
                      {client.archived_at && <Badge variant="muted">Archived</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
